/**
 * `wpp::tree`
 *
 * Selection for the whole tree. Items ask it whether they are selected rather
 * than tracking their own, so that single-select actually deselects the previous
 * item — a tree of items each holding a boolean cannot do that.
 *
 * Keyboard navigation, per the WAI-ARIA tree pattern. This is not a refinement:
 * before it, every node carried `tabindex="-1"` and the `<li>` that owns the
 * `treeitem` role carried none, so the tree could not be reached by keyboard at
 * all. A tree is one tab stop, and the arrows move within it — which is why the
 * roving `tabindex` and the arrow keys have to arrive together. One without the
 * other is worse than neither.
 *
 * Expanding and collapsing goes through the item's own control rather than
 * reaching into its Alpine scope, so there is one path that opens a branch.
 *
 * @param {{ allowMultiple?: boolean, selectedItems?: Array }} options
 */
export default (options = {}) => ({
    allowMultiple: options.allowMultiple ?? false,
    selectedItems: options.selectedItems ?? [],

    init() {
        this.$nextTick(() => this.refreshTabStops());
    },

    /**
     * The nodes a user can actually move to. A collapsed branch's children are
     * `display: none`, which is what `offsetParent` is being asked about.
     */
    visibleNodes() {
        return Array.from(this.$el.querySelectorAll('[role="treeitem"]'))
            .filter((node) => node.offsetParent !== null);
    },

    /** Exactly one node is tabbable at a time; the rest are reachable by arrow. */
    refreshTabStops(focused = null) {
        const nodes = this.visibleNodes();

        if (nodes.length === 0) {
            return;
        }

        const current = focused ?? nodes.find((node) => node.tabIndex === 0) ?? nodes[0];

        nodes.forEach((node) => {
            node.tabIndex = node === current ? 0 : -1;
        });
    },

    /** Clicking a node moves the tab stop to it, so Tab returns where you were. */
    onFocusIn(event) {
        const node = event.target.closest('[role="treeitem"]');

        if (node) {
            this.refreshTabStops(node);
        }
    },

    focusNode(node) {
        if (! node) {
            return;
        }

        this.refreshTabStops(node);
        node.focus();
    },

    onKeydown(event) {
        const node = event.target.closest('[role="treeitem"]');

        if (! node) {
            return;
        }

        const nodes = this.visibleNodes();
        const index = nodes.indexOf(node);
        const branch = node.hasAttribute('aria-expanded');
        const expanded = node.getAttribute('aria-expanded') === 'true';
        const control = node.querySelector('button, a');

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.focusNode(nodes[index + 1]);
                break;

            case 'ArrowUp':
                event.preventDefault();
                this.focusNode(nodes[index - 1]);
                break;

            case 'Home':
                event.preventDefault();
                this.focusNode(nodes[0]);
                break;

            case 'End':
                event.preventDefault();
                this.focusNode(nodes[nodes.length - 1]);
                break;

            /*
             * Right opens a closed branch and, on an open one, steps into it —
             * two presses to reach a child, which is what the pattern specifies
             * and what makes the tree navigable without looking.
             */
            case 'ArrowRight':
                event.preventDefault();

                if (branch && ! expanded) {
                    control?.click();
                } else if (branch && expanded) {
                    this.focusNode(nodes[index + 1]);
                }
                break;

            // Left closes an open branch, and otherwise goes up to the parent.
            case 'ArrowLeft':
                event.preventDefault();

                if (branch && expanded) {
                    control?.click();
                } else {
                    this.focusNode(node.parentElement?.closest('[role="treeitem"]'));
                }
                break;

            case 'Enter':
            case ' ':
                event.preventDefault();
                control?.click();
                break;
        }
    },

    toggleItem(value) {
        if (this.allowMultiple) {
            const index = this.selectedItems.indexOf(value);

            if (index === -1) {
                this.selectedItems.push(value);
            } else {
                this.selectedItems.splice(index, 1);
            }

            return;
        }

        this.selectedItems = this.isSelected(value) ? [] : [value];
    },

    isSelected(value) {
        return this.selectedItems.includes(value);
    },
});
